import { Cookies } from 'react-cookie';
import {
  BadRequestError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
} from './errorTypes';
import type { ActionPrompt } from 'types/common/ErrorResponse';

export async function handleResponse<TData>(
  response: Response,
  callback?: (res: any) => void
): Promise<TData> {
  if (response.status === 401) {
    const cookies = new Cookies();
    cookies.remove('userToken', { path: '/' });
    cookies.remove('userID', { path: '/' });

    throw new UnauthorizedError();
  }

  if (response.status === 204) {
    return {} as TData;
  }

  const res = await response.json();

  if (callback) {
    callback(res);
  }

  if (response.status === 400) {
    throw new BadRequestError(res?.errors?.[0].title);
  }

  if (response.status === 403) {
    const cookies = new Cookies();
    const error = res?.errors?.[0];
    const errorActionPrompt = error?.meta?.actionPrompt as ActionPrompt;
    if (!!errorActionPrompt) {
      cookies.set('errorActionPrompt', JSON.stringify(errorActionPrompt), {
        path: '/',
      });
    }
    throw new ForbiddenError(res?.errors?.[0].title);
  }

  if (response.status === 404) {
    throw new NotFoundError(res?.errors?.[0].title);
  }

  if (response.status === 422) {
    const cookies = new Cookies();
    const error = res?.errors?.[0];
    const errorActionPrompt = error?.meta?.actionPrompt as ActionPrompt;
    if (!!errorActionPrompt) {
      cookies.set('errorActionPrompt', JSON.stringify(errorActionPrompt), {
        path: '/',
      });
    }
    throw new Error(res?.errors?.[0].title);
  }

  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      `There has been an error. Response status: ${response.status}`
    );
  }

  return res;
}

type FetchWrapperResponseType<TData, TRaw extends boolean> = TRaw extends true
  ? Response
  : TData;

export async function fetchWrapper<
  TData,
  TRaw extends boolean = false,
  TBody = unknown
>({
  method = 'GET',
  url,
  body,
  // token,
  additionalHeaders,
  rawResponse = false as TRaw,
  ...additionalOptions
}: {
  url: string;
  method?: string;
  body?: TBody;
  token?: string;
  additionalHeaders?: {
    Accept: string;
    'Content-Type': string;
  };
  additionalOptions?: RequestInit;
  rawResponse?: TRaw;
}): Promise<FetchWrapperResponseType<TData, TRaw>> {
  const cookies = new Cookies();
  const _token = cookies.get('userToken');
  const adminAccessToken = process.env.REACT_APP_RESTRICTED_ADMIN_TOKEN;
  const token = process.env.REACT_APP_RESTRICTED_ADMIN_TOKEN;
  const API_BASEURL = process.env.REACT_APP_API_BASEURL;
  const userToken = _token || adminAccessToken || token;
  const _url = `${API_BASEURL}${url}`;
  const options = {
    method: method,
    headers: {
      Accept: additionalHeaders?.Accept || 'application/vnd.api+json',
      'Content-Type':
        additionalHeaders?.['Content-Type'] || 'application/vnd.api+json',
      Cache: 'no-cache',
      Authorization: `Bearer ${userToken}`,
    },
    credentials: 'include' as RequestCredentials,
    body: body && JSON.stringify(body),
    ...additionalOptions,
  };
  if (body) {
    console.info('payload', JSON.stringify(body, null, 2));
  }
  const response = await fetch(_url, options);

  if (rawResponse) {
    return response as FetchWrapperResponseType<TData, TRaw>;
  }

  return (await handleResponse<TData>(response)) as FetchWrapperResponseType<
    TData,
    TRaw
  >;
}
